// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * A distance, typed the way people type distances.
 *
 * Calibration asks "how far is that really?", and the honest answer on a
 * construction drawing is written half a dozen ways: 100, 100.5, 100', 100'-6",
 * 100' 6", 6". Rejecting all but one of those, or worse, silently reading
 * 100'-6" as 100, turns a careful measurement into a wrong scale that
 * everything else on the sheet then inherits.
 */

/**
 * Feet, from whatever was typed, or null when it cannot be read.
 *
 * Null rather than a guess: a scale set from a misread number is wrong
 * everywhere and invisible, and the caller can say so instead.
 */
export function parseFeet(raw: string | number | null | undefined): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) && raw > 0 ? raw : null;
  const s = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (!s) return null;

  // Plain number: feet, possibly decimal.
  if (/^\d+(\.\d+)?$/.test(s)) {
    const v = Number(s);
    return v > 0 ? v : null;
  }

  // Inches alone: 6", 6 in
  const inchOnly = s.match(/^(\d+(?:\.\d+)?)\s*(?:"|in|inch(?:es)?)$/i);
  if (inchOnly) {
    const v = Number(inchOnly[1]) / 12;
    return v > 0 ? v : null;
  }

  // Feet, then optionally inches: 100', 100 ft, 100'-6", 100' 6", 100'6"
  const m = s.match(
    /^(\d+(?:\.\d+)?)\s*(?:'|ft|feet|foot)?\s*(?:[-\s]\s*)?(?:(\d+(?:\.\d+)?)\s*(?:"|in|inch(?:es)?)?)?$/i,
  );
  if (!m) return null;
  // A bare "100 6" is ambiguous and is not accepted as 100'-6"; the foot mark
  // has to be there for the second number to mean inches.
  if (m[2] !== undefined && !/['′]|ft|feet|foot/i.test(s)) return null;

  const feet = Number(m[1]);
  const inches = m[2] === undefined ? 0 : Number(m[2]);
  if (!Number.isFinite(feet) || !Number.isFinite(inches)) return null;
  if (inches >= 12) return null;                 // 100'-13" is a typo, not a length
  const total = feet + inches / 12;
  return total > 0 ? total : null;
}

/** What the field should say it will accept. */
export const FEET_HINT = `e.g. 100, 100.5, 100'-6"`;
